import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '@/lib/supabase';
import { Fluid, FluidWithMedia, FluidSummary } from '@/types/fluid';
import { getSignedViewUrl } from '@/lib/getSignedViewUrl';

interface FluidContextType {
  fluids: FluidWithMedia[];
  fluidSummaries: FluidSummary[];
  loading: boolean;
  error: string | null;
  fetchFluidsForCar: (carId: string) => Promise<void>;
  addFluid: (fluid: Omit<Fluid, 'id' | 'created_at' | 'updated_at'>) => Promise<void>;
  updateFluid: (id: string, updates: Partial<Fluid>) => Promise<void>;
  deleteFluid: (id: string) => Promise<void>;
  getFluidSummaries: (carId: string) => Promise<FluidSummary[]>;
}

const FluidContext = createContext<FluidContextType | undefined>(undefined);

export const useFluid = () => {
  const context = useContext(FluidContext);
  if (!context) {
    throw new Error('useFluid must be used within a FluidProvider');
  }
  return context;
};

interface FluidProviderProps {
  children: ReactNode;
}

export const FluidProvider: React.FC<FluidProviderProps> = ({ children }) => {
  const [fluids, setFluids] = useState<FluidWithMedia[]>([]);
  const [fluidSummaries, setFluidSummaries] = useState<FluidSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchFluidsForCar = async (carId: string) => {
    try {
      setLoading(true);
      setError(null);

      const { data, error } = await supabase
        .from('fluids')
        .select('*')
        .eq('car_id', carId)
        .order('action_date', { ascending: false });

      if (error) throw error;

      // Process fluids with signed URLs for media
      const fluidsWithMedia = await Promise.all(
        (data || []).map(async (fluid) => {
          let mediaUrl: string | undefined;
          if (fluid.media_path) {
            try {
              const signedUrlInfo = await getSignedViewUrl(fluid.media_path);
              mediaUrl = signedUrlInfo.url;
            } catch (err) {
              console.warn('Failed to get signed URL for fluid media:', err);
            }
          }
          return { ...fluid, media_url: mediaUrl };
        })
      );

      setFluids(fluidsWithMedia);
    } catch (err) {
      console.error('Error fetching fluids:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch fluids');
    } finally {
      setLoading(false);
    }
  };

  const getFluidSummaries = async (carId: string): Promise<FluidSummary[]> => {
    try {
      const { data, error } = await supabase
        .from('fluids')
        .select('*')
        .eq('car_id', carId)
        .order('action_date', { ascending: false });

      if (error) throw error;

      // Group by fluid type and get latest info
      const summaries: FluidSummary[] = [];
      const fluidTypes = ['engine_oil', 'brake_fluid', 'coolant', 'washer_fluid'];
      
      for (const fluidType of fluidTypes) {
        const typeFluids = (data || []).filter(f => f.fluid_type === fluidType);
        
        if (typeFluids.length > 0) {
          const latest = typeFluids[0];
          const lastRefill = typeFluids.find(f => f.action_type === 'refill');
          const lastReplacement = typeFluids.find(f => f.action_type === 'replacement');
          
          let mediaUrl: string | undefined;
          if (latest.media_path) {
            try {
              const signedUrlInfo = await getSignedViewUrl(latest.media_path);
              mediaUrl = signedUrlInfo.url;
            } catch (err) {
              console.warn('Failed to get signed URL for fluid media:', err);
            }
          }

          // Fetch odometer readings linked to last refill/replacement via related_id (if present)
          let refillOdometer: number | undefined;
          let replacementOdometer: number | undefined;
          try {
            if (lastRefill?.id) {
              const { data: odoRefill } = await supabase
                .from('odometer_readings')
                .select('reading, related_id')
                .eq('car_id', carId)
                .eq('related_id', lastRefill.id)
                .limit(1)
                .maybeSingle();
              refillOdometer = odoRefill?.reading ?? undefined;
            }
            if (lastReplacement?.id) {
              const { data: odoReplace } = await supabase
                .from('odometer_readings')
                .select('reading, related_id')
                .eq('car_id', carId)
                .eq('related_id', lastReplacement.id)
                .limit(1)
                .maybeSingle();
              replacementOdometer = odoReplace?.reading ?? undefined;
            }
          } catch (e) {
            // If table missing or no rows, ignore
          }

          summaries.push({
            fluid_type: fluidType,
            latest_spec_code: latest.spec_code,
            latest_color: latest.color,
            latest_media_url: mediaUrl,
            last_refill_date: lastRefill?.action_date,
            last_replacement_date: lastReplacement?.action_date,
            last_refill_odometer: refillOdometer,
            last_replacement_odometer: replacementOdometer,
          });
        }
      }

      return summaries;
    } catch (err) {
      console.error('Error getting fluid summaries:', err);
      return [];
    }
  };

  const addFluid = async (fluid: Omit<Fluid, 'id' | 'created_at' | 'updated_at'>) => {
    try {
      setLoading(true);
      setError(null);

      const { data, error } = await supabase
        .from('fluids')
        .insert([fluid])
        .select()
        .single();

      if (error) throw error;

      // Refresh fluids for the car
      await fetchFluidsForCar(fluid.car_id);
      
      // Also refresh fluid summaries
      const summaries = await getFluidSummaries(fluid.car_id);
      setFluidSummaries(summaries);
    } catch (err) {
      console.error('Error adding fluid:', err);
      setError(err instanceof Error ? err.message : 'Failed to add fluid');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateFluid = async (id: string, updates: Partial<Fluid>) => {
    try {
      setLoading(true);
      setError(null);

      const { error } = await supabase
        .from('fluids')
        .update(updates)
        .eq('id', id);

      if (error) throw error;

      // Refresh fluids
      const updatedFluid = fluids.find(f => f.id === id);
      if (updatedFluid) {
        await fetchFluidsForCar(updatedFluid.car_id);
      }
    } catch (err) {
      console.error('Error updating fluid:', err);
      setError(err instanceof Error ? err.message : 'Failed to update fluid');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const deleteFluid = async (id: string) => {
    try {
      setLoading(true);
      setError(null);

      const fluidToDelete = fluids.find(f => f.id === id);
      if (!fluidToDelete) return;

      const { error } = await supabase
        .from('fluids')
        .delete()
        .eq('id', id);

      if (error) throw error;

      // Refresh fluids for the car
      await fetchFluidsForCar(fluidToDelete.car_id);
    } catch (err) {
      console.error('Error deleting fluid:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete fluid');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const value: FluidContextType = {
    fluids,
    fluidSummaries,
    loading,
    error,
    fetchFluidsForCar,
    addFluid,
    updateFluid,
    deleteFluid,
    getFluidSummaries,
  };

  return (
    <FluidContext.Provider value={value}>
      {children}
    </FluidContext.Provider>
  );
};


